import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|History|Benefits|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Benefit,
	portal: 'SP',
};
const columnHeadingDescriptionStart = 'Column heading for: ';

export type SPCandidateBenefitsLocNames =
	| 'SPCandidateBenefitsTitleTab'
	| 'SPCandidateBenefitsContentHasLineupDp'
	| 'SPCandidateBenefitsContentNoLineupDp'
	| 'SPCandidateBenefitsNotificationChangesSaved'
	| 'SPCandidateBenefitsNotificationBenefitClaimed'
	| 'SPCandidateBenefitsNotificationNoVouchersAvailable'
	| 'SPCandidateBenefitsLabelFilterByStatus'
	| 'SPCandidateBenefitsLabelFilterByType'
	| 'SPCandidateBenefitsLabelFilterByGroup'
	| 'SPCandidateBenefitsLabelStatus'
	| 'SPCandidateBenefitsLabelGroup'
	| 'SPCandidateBenefitsLabelName'
	| 'SPCandidateBenefitsLabelExpireDate'
	| 'SPCandidateBenefitsLabelType'
	| 'SPCandidateBenefitsLabelUrl'
	| 'SPCandidateBenefitsLabelCode'
	| 'SPCandidateBenefitsContentApplyLink'
	| 'SPCandidateBenefitsContentNoToken'
	| 'SPCandidateBenefitsContentTokenNotValid'
	| 'SPCandidateBenefitsButtonReassign'
	| 'SPCandidateBenefitsButtonClaim'
	| 'SPCandidateBenefitsImpersonateError'
	| 'SPCandidateBenefitsContentInstructions'
	| '928b2623e68334241167cd0bc9c1b08d';

export const spCandidateBenefitsLocs: LocDefs<SPCandidateBenefitsLocNames> = {
	SPCandidateBenefitsTitleTab: {
		defaultValue: 'Benefits',
		description: `Tab Title`,
		displayPath: `${pathStart}Tab Title`,
		displayType: 'Title',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.Title.Tab',
		...sharedProperties,
	},
	SPCandidateBenefitsContentHasLineupDp: {
		defaultValue:
			'The candidate completed the opt-in process. An account was successfully created at Lineup',
		description: `Message shown when the Lineup process is complete`,
		displayPath: `${pathStart}Lineup Completed`,
		displayType: 'Content',
		legacyName: 'Benefit.Lineup/Candidates.ProgramHistory.Benefits.Content.HasLineupDP',
		shared: false,
		htmlAllowed: false,
		portal: 'SP',
		feature: HubFeature.BenefitLineup,
	},
	SPCandidateBenefitsContentNoLineupDp: {
		defaultValue:
			'The candidate has not completed the opt-in process to create a Lineup Account.',
		description: `Message shown when the Lineup process is not complete`,
		displayPath: `${pathStart}Lineup Not Completed`,
		displayType: 'Content',
		legacyName: 'Benefit.Lineup/Candidates.ProgramHistory.Benefits.Content.NoLineupDP',
		shared: false,
		htmlAllowed: false,
		portal: 'SP',
		feature: HubFeature.BenefitLineup,
	},
	SPCandidateBenefitsNotificationChangesSaved: {
		defaultValue: 'Changes Saved',
		description: `Text for Changes Saved.`,
		displayPath: `${pathStart}Changes Saved`,
		displayType: 'Notification',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.NotificationMessage.Reassigned',
		...sharedProperties,
	},
	SPCandidateBenefitsNotificationBenefitClaimed: {
		defaultValue: 'Benefit Claimed',
		description: `Text for Benefit Claimed.`,
		displayPath: `${pathStart}Benefit Claimed`,
		displayType: 'Notification',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.NotificationMessage.Claimed',
		...sharedProperties,
	},
	SPCandidateBenefitsNotificationNoVouchersAvailable: {
		defaultValue: 'No vouchers available',
		description: `Text for No vouchers available notification.`,
		displayPath: `${pathStart}No vouchers available`,
		displayType: 'Notification',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.NotificationMessage.NoVouchers',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelFilterByStatus: {
		defaultValue: 'Filter by Status',
		description: `Label for: Filter by Status.`,
		displayPath: `${pathStart}Filter by Status`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.FieldLabel.StatusFilter',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelFilterByType: {
		defaultValue: 'Filter by Type',
		description: `Label for: Filter by Type.`,
		displayPath: `${pathStart}Filter by Type`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.FieldLabel.TypeFilter',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelFilterByGroup: {
		defaultValue: 'Filter by Group',
		description: `Label for: Filter by Group.`,
		displayPath: `${pathStart}Filter by Group`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.FieldLabel.GroupFilter',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelStatus: {
		defaultValue: 'Status',
		description: `${columnHeadingDescriptionStart}Status.`,
		displayPath: `${pathStart}Status`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.ColumnHeading.Status',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelGroup: {
		defaultValue: 'Group',
		description: `${columnHeadingDescriptionStart}Group.`,
		displayPath: `${pathStart}Group`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.ColumnHeading.Group',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelName: {
		defaultValue: 'Name',
		description: `${columnHeadingDescriptionStart}Name.`,
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.ColumnHeading.Name',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelExpireDate: {
		defaultValue: 'Expire Date',
		description: `${columnHeadingDescriptionStart}Expire Date.`,
		displayPath: `${pathStart}Expire Date`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.ColumnHeading.ExpireDate',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelType: {
		defaultValue: 'Type',
		description: `${columnHeadingDescriptionStart}Type.`,
		displayPath: `${pathStart}Type`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.ColumnHeading.Type',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelUrl: {
		defaultValue: 'Apply Link',
		description: `${columnHeadingDescriptionStart}Apply Link.`,
		displayPath: `${pathStart}Apply Link`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.ColumnHeading.ApplyLink',
		...sharedProperties,
	},
	SPCandidateBenefitsLabelCode: {
		defaultValue: 'Code',
		description: `${columnHeadingDescriptionStart}Code.`,
		displayPath: `${pathStart}Code`,
		displayType: 'Label',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.ColumnHeading.Code',
		...sharedProperties,
	},
	SPCandidateBenefitsContentApplyLink: {
		defaultValue: 'Preview',
		description: `Text for Preview link.`,
		displayPath: `${pathStart}Preview`,
		displayType: 'Content',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.Content.ApplyLink',
		...sharedProperties,
	},
	SPCandidateBenefitsContentNoToken: {
		defaultValue: 'No token available',
		description: `Text for No token available.`,
		displayPath: `${pathStart}No token available`,
		displayType: 'Content',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.Content.NoToken',
		...sharedProperties,
	},
	SPCandidateBenefitsContentTokenNotValid: {
		defaultValue: 'Token is no longer valid',
		description: `Text for Token is no longer valid.`,
		displayPath: `${pathStart}Token is no longer valid`,
		displayType: 'Content',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.Content.TokenStatus',
		...sharedProperties,
	},
	SPCandidateBenefitsButtonReassign: {
		defaultValue: 'Assign new voucher',
		description: `Assign new voucher button.`,
		displayPath: `${pathStart}Assign new voucher`,
		displayType: 'Button',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.Button.Reassign',
		...sharedProperties,
	},
	SPCandidateBenefitsButtonClaim: {
		defaultValue: 'Claim Benefit',
		description: `Claim Benefit button.`,
		displayPath: `${pathStart}Claim Benefit`,
		displayType: 'Button',
		legacyName: 'Benefit/Candidates.ProgramHistory.Benefits.Button.Claim',
		...sharedProperties,
	},
	SPCandidateBenefitsImpersonateError: {
		defaultValue: 'Error impersonating candidate',
		description: `Error message for impersonating candidate.`,
		displayPath: `${pathStart}Impersonate Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPCandidateBenefitsContentInstructions: {
		defaultValue: '',
		description: `Instructions for the page`,
		displayPath: `${pathStart}Instructions`,
		displayType: 'Content',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.Benefit,
		portal: 'SP',
	},
	'928b2623e68334241167cd0bc9c1b08d': {
		defaultValue: 'Benefit has already been claimed',
		description: `Error message when attempting to claim a benefit that has already been claimed.`,
		displayPath: `${pathStart}Benefit Already Claimed`,
		displayType: 'Error',
		...sharedProperties,
	},
};

export type SPCandidateBenefitsLocType = LocMap<SPCandidateBenefitsLocNames>;
